import * as z from 'zod'
import { zodQs } from '../utils'

// --- Provider enum ---

export enum IntegrationProvider {
    HubSpot = 'hubspot',
    Salesforce = 'salesforce',
    SharePoint = 'sharepoint',
}

/**
 * Providers whose credential is a delegated consent rather than a form.
 *
 * They are never created or edited through the integration DTO: the row is
 * written by the OAuth callback, which is the only place that holds a refresh
 * token. Everything else about them (listing, enabling, testing, deleting) is
 * the ordinary integration machinery.
 */
export const OAUTH_INTEGRATION_PROVIDERS: IntegrationProvider[] = [
    IntegrationProvider.SharePoint,
]

export function isOAuthIntegrationProvider(
    provider: IntegrationProvider,
): boolean {
    return OAUTH_INTEGRATION_PROVIDERS.includes(provider)
}

export enum IntegrationSyncStatus {
    Pending = 'pending',
    Success = 'success',
    Failed = 'failed',
}

// --- Field mapping transforms ---

export enum FieldMappingTransform {
    SplitFirstLast = 'split_first_last',
    MapValues = 'map_values',
    JoinArray = 'join_array',
    ExtractDomainCompany = 'extract_domain_company',
    None = 'none',
}

// --- Field mapping ---

export const FieldMappingItemSchema = z.object({
    source: z.string(),
    targetFields: z.array(z.string()).min(1),
    transform: z.enum(FieldMappingTransform).optional(),
    valueMap: z.record(z.string(), z.string()).optional(),
    fallback: z.string().optional(),
})

export type FieldMappingItem = z.infer<typeof FieldMappingItemSchema>

// --- Provider-specific config ---

export const SalesforceConfigSchema = z.object({
    instanceUrl: z.url(),
    clientId: z.string().min(1),
    clientSecret: z.string().min(1),
    apiVersion: z.string().default('v61.0'),
    externalIdField: z.string().default('Email'),
    // OAuth 2.0 client credentials flow tokens are refreshed at runtime
})

export const HubSpotConfigSchema = z.object({
    accessToken: z.string().min(1),
    portalId: z.string().optional(),
    defaultOwnerId: z.string().optional(),
    staticProperties: z
        .array(z.object({ key: z.string(), value: z.string() }))
        .optional()
        .default([]),
})

/**
 * What the SharePoint consent leaves in the row.
 *
 * `refreshToken` is the credential (encrypted at rest, stripped from every
 * response); the account fields come from the id token and exist so the card
 * can say whose consent this is, which is the only way an administrator can
 * tell that the sync stopped because that person left. `lastRefreshAt` and
 * `lastRefreshError` are the connection's real state: `enabled` says whether
 * we should use it, not whether Microsoft still accepts it.
 */
export const SharePointConfigSchema = z.object({
    refreshToken: z.string().min(1),
    tenantId: z.string().min(1),
    accountId: z.string().min(1),
    accountEmail: z.string().optional(),
    accountName: z.string().optional(),
    scope: z.string().optional(),
    connectedAt: z.string().optional(),
    /** When a refresh last *succeeded*. A failure does not move it. */
    lastRefreshAt: z.string().optional(),
    lastRefreshError: z.string().nullable().optional(),
    /**
     * Whether the last failure was the consent going away, as opposed to a bad
     * configuration or an authority having a bad day. Only the first is fixed
     * by reconnecting, and telling an administrator to reconnect when it would
     * change nothing is how a card stops being believed.
     */
    lastRefreshRevoked: z.boolean().optional(),
})

export const IntegrationConfigSchema = z.discriminatedUnion('provider', [
    z.object({
        provider: z.literal('salesforce'),
        ...SalesforceConfigSchema.shape,
    }),
    z.object({ provider: z.literal('hubspot'), ...HubSpotConfigSchema.shape }),
    z.object({
        provider: z.literal('sharepoint'),
        ...SharePointConfigSchema.shape,
    }),
])

export type IntegrationConfig = z.infer<typeof IntegrationConfigSchema>

// --- OrganizationIntegration ---

export const OrganizationIntegrationSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    provider: z.enum(IntegrationProvider),
    enabled: z.boolean(),
    // config is returned with secrets masked
    config: z.record(z.string(), z.unknown()).default({}),
    fieldMapping: z.array(FieldMappingItemSchema).default([]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    createdBy: z.uuid().nullable(),
    updatedBy: z.uuid().nullable(),
})

export type OrganizationIntegration = z.infer<
    typeof OrganizationIntegrationSchema
>

export const OrganizationIntegrationDtoSchema = z
    .object({
        organizationId: z.uuid().optional(),
        provider: z
            .enum(IntegrationProvider)
            .default(IntegrationProvider.HubSpot),
        config: z.record(z.string(), z.unknown()).default({}),
        fieldMapping: z.array(FieldMappingItemSchema).default([]),
        enabled: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
        // An OAuth provider has no form to fill: its row is written by the
        // callback, with a refresh token this endpoint never sees. Without
        // this branch the config would be validated against HubSpot's schema,
        // which is what the fallback below used to do for every provider that
        // was not Salesforce, and the caller would be told to send an access
        // token that does not exist.
        if (isOAuthIntegrationProvider(data.provider)) {
            ctx.addIssue({
                code: 'custom',
                path: ['provider'],
                message: `${data.provider} is connected through OAuth, not through this endpoint`,
            })
            return
        }

        const schema =
            data.provider === IntegrationProvider.Salesforce
                ? SalesforceConfigSchema
                : HubSpotConfigSchema

        const result = schema.safeParse(data.config)
        if (!result.success) {
            for (const issue of result.error.issues) {
                ctx.addIssue({
                    ...issue,
                    path: ['config', ...issue.path],
                })
            }
        }
    })

export type OrganizationIntegrationDto = z.infer<
    typeof OrganizationIntegrationDtoSchema
>

export const OrganizationIntegrationUpdateSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    config: z.record(z.string(), z.unknown()).optional(),
    fieldMapping: z.array(FieldMappingItemSchema).optional(),
})

export type OrganizationIntegrationUpdate = z.infer<
    typeof OrganizationIntegrationUpdateSchema
>

export const OrganizationIntegrationToggleSchema = z.object({
    enabled: z.boolean(),
})

export type OrganizationIntegrationToggle = z.infer<
    typeof OrganizationIntegrationToggleSchema
>

export const OrganizationIntegrationsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['createdAt', 'updatedAt', 'provider']),
    ...zodQs.ids(),
    organizationId: z.uuid().optional(),
    provider: z.enum(IntegrationProvider).optional(),
})

export type OrganizationIntegrationsQuerystring = z.infer<
    typeof OrganizationIntegrationsQuerystringSchema
>

// --- IntegrationSyncLog ---

export const IntegrationSyncLogSchema = z.object({
    id: z.uuid(),
    organizationId: z.uuid(),
    integrationId: z.uuid(),
    leadId: z.uuid(),
    provider: z.enum(IntegrationProvider),
    status: z.enum(IntegrationSyncStatus),
    externalId: z.string().nullable(),
    errorMessage: z.string().nullable(),
    attempts: z.number().int(),
    payload: z.record(z.string(), z.unknown()).nullable(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
})

export type IntegrationSyncLog = z.infer<typeof IntegrationSyncLogSchema>

export const IntegrationSyncLogsQuerystringSchema = z.object({
    ...zodQs.pagination(),
    ...zodQs.sort(['createdAt', 'updatedAt', 'status']),
    ...zodQs.ids(),
    integrationId: z.uuid().optional(),
    leadId: z.uuid().optional(),
    status: z.enum(IntegrationSyncStatus).optional(),
})

export type IntegrationSyncLogsQuerystring = z.infer<
    typeof IntegrationSyncLogsQuerystringSchema
>
